/**
 * RawBundleView — R12 "View raw data" toggle. Fetches the stored FHIR bundle on demand
 * (first open only; never on render), shows it pretty-printed in a scroll-bounded box.
 * Visible only with the HIU_VIEW_RAW permission (matches the backend route guard).
 */
import React, { useState } from 'react';

import { usePermission } from '../../context/PermissionContext';
import { getRawRecord } from '../../services/api/hiuApi';
import { OTHER_ORGS } from '../../constants/abdmCopy';

const MAX_STRING = 120;

// Display only: long strings (base64 attachment data) are shortened.
export function shortenLongStrings(value) {
  if (typeof value === 'string') {
    return value.length > MAX_STRING
      ? `${value.slice(0, 40)}… (${value.length} characters, shortened)`
      : value;
  }
  if (Array.isArray(value)) return value.map(shortenLongStrings);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, shortenLongStrings(v)]));
  }
  return value;
}

function errorCopy(err) {
  if (err.code === 'CONSENT_NOT_ACTIVE') return 'Raw data is not available because the consent is no longer active.';
  if (err.code === 'FORBIDDEN' || err.status === 403) return OTHER_ORGS.rawForbidden;
  if (err.code === 'BUNDLE_NOT_AVAILABLE') return 'The raw data for this record is not available.';
  if (err.status === 413) return 'This record is too large to show as raw data.';
  return err.userMessage || 'Could not load raw data.';
}

export default function RawBundleView({ fetchId, recordId, isDark }) {
  const canViewRaw = usePermission('HIU_VIEW_RAW');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState(null);
  const [error, setError] = useState('');

  if (!canViewRaw) return null;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || text !== null || loading) return;
    setLoading(true);
    setError('');
    try {
      const raw = await getRawRecord(fetchId, recordId);
      setText(JSON.stringify(shortenLongStrings(raw.bundle), null, 2));
    } catch (err) {
      setError(errorCopy(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={toggle}
        aria-expanded={open}
        className="px-3 py-1.5 rounded-md border text-sm"
        style={{
          border: `1px solid ${isDark ? '#475569' : '#94a3b8'}`,
          background: isDark ? '#1e293b' : '#ffffff',
          color: isDark ? '#f1f5f9' : '#1e293b',
        }}
      >
        {open ? 'Hide raw data' : 'View raw data'}
      </button>
      {open && loading && <p style={{ fontSize: 12, color: isDark ? '#9ca3af' : '#6b7280' }}>Loading…</p>}
      {open && error && <p style={{ fontSize: 12, color: isDark ? '#f87171' : '#dc2626' }}>{error}</p>}
      {open && text !== null && (
        <pre
          data-testid="raw-bundle"
          style={{
            marginTop: 8, maxHeight: 360, overflow: 'auto', fontSize: 12, padding: 12, borderRadius: 8,
            background: isDark ? '#0f172a' : '#f3f4f6', color: isDark ? '#e2e8f0' : '#111827',
          }}
        >
          {text}
        </pre>
      )}
    </div>
  );
}
